import { ApiError } from '@app/api/errors';
import MultiTagsPicker from '@app/components/multi-tag-picker';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useDeleteImport,
  useUpdateImport,
} from '@shared/api/hooks/ccf/imports';
import { ActivityImportList } from '@shared/api/types';
import Text from '@shared/components/content/text';
import InputFormField from '@shared/components/form/input-form-field';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useForm } from 'react-hook-form';
import { array, object, string, z } from 'zod';

const activityImportEditFormSchema = object({
  fileName: string({
    required_error: 'Please provide a filename',
  }).min(1, 'Filename is required'),
  tags: array(string()).optional(),
});

interface ActivityImportEditFormProps {
  importData?: ActivityImportList;
  closeDialog: () => void;
}

export default function ActivityImportEditForm({
  importData,
  closeDialog,
}: ActivityImportEditFormProps) {
  const { toast } = useToast();

  const { mutateAsync: updateImport, isLoading: isUpdating } =
    useUpdateImport();
  const { mutateAsync: deleteImport, isLoading: isDeleting } =
    useDeleteImport();

  const isSubmitting = isUpdating || isDeleting;

  const form = useForm<z.infer<typeof activityImportEditFormSchema>>({
    resolver: zodResolver(activityImportEditFormSchema),
    defaultValues: {
      fileName: importData?.fileName || '',
      tags: importData?.tags?.map((tag) => tag.uuid) || [],
    },
  });

  const { handleSubmit, reset } = form;

  const onSubmit = async (
    data: z.infer<typeof activityImportEditFormSchema>
  ) => {
    if (!importData?.uuid) return;

    try {
      await updateImport({
        activityImportUuid: importData.uuid,
        data: {
          fileName: data.fileName,
          tags: data.tags,
        },
      });

      toast({
        title: 'Import updated',
        variant: 'success',
      });
      closeDialog();
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Error updating import',
        description: 'Please refresh and try again',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!importData?.uuid) return;

    try {
      await deleteImport({
        activityImportUuid: importData.uuid,
      });

      toast({
        title: 'Import deleted',
        variant: 'success',
      });
      closeDialog();
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.type === 'purchased-good-linked-to-sold-good'
      ) {
        toast({
          title: 'Cannot delete this import',
          description:
            'This import contains a purchased good linked to one or more sold goods. Remove the link before deleting.',
          variant: 'destructive',
        });
      } else {
        sentry.log(error);
        toast({
          title: 'Error deleting import',
          description: 'Please refresh and try again',
          variant: 'destructive',
        });
      }
    }
  };

  if (!importData) {
    return (
      <div className="p-4">
        <Text>No import data available</Text>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <InputFormField
          name="fileName"
          label="Name"
          placeholder="Enter filename"
          disabled={isSubmitting}
        />
        <MultiTagsPicker
          name="tags"
          label="Tags"
          allowCreate
          placeholder="Select or create tags"
          disabled={isSubmitting}
        />
        <div className="mt-12 space-y-4">
          <Text variant="subtle" className="mb-2 text-xs">
            Deleting this import will also remove the activity data from your
            account. When updating or deleting, you may need to republish any
            reports that include data from within this import.
          </Text>
          <div className="flex justify-between">
            <div>
              <Button
                type="button"
                size="lg"
                variant="outline-destructive"
                loading={isDeleting}
                disabled={isSubmitting}
                onClick={handleDelete}
              >
                Delete import
              </Button>
            </div>
            <div className="flex space-x-2">
              <Button
                type="button"
                size="lg"
                variant="secondary"
                onClick={() => {
                  closeDialog();
                  reset();
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="lg"
                loading={isUpdating}
                disabled={isSubmitting}
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
